import todoIcon from "@/assets/icons/todos.png";
import { ScrollArea } from "@/components/ui/scroll-area";
import ExpandableCard from "@/components/widgets/dashboard/ExpandableCard";
import TodoItem from "@/domain/entities/TodoItem";
import useGetDashboardTodos from "@/hooks/useGetDashboardTodos";
import useManageTodo from "@/hooks/useManageTodo";
import { queryKeys } from "@/lib/queryKeys";
import mailStore from "@/stores/mailStore";
import { useQueryClient } from "@tanstack/react-query";
import { useStore } from "@tanstack/react-store";
import { useTranslation } from "react-i18next";
import DashboardTodoLine from "./DashboardTodoLine";

type DashboardTodosProps = {
    isExpanded: boolean;
    onExpand: () => void;
    onCollapse: () => void;
};

const DashboardTodos = ({
    isExpanded,
    onExpand,
    onCollapse,
}: DashboardTodosProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const { setTodoState } = useManageTodo();
    const { data: todos } = useGetDashboardTodos();

    const openedMail = useStore(mailStore, (state) => state.openedMail);

    const handleCompletionToggle = async (todoToUpdate: TodoItem) => {
        if (!todos) return;

        const previousTodos = [...todos];
        const newAcceptedTodos = todos.map((todo: TodoItem) =>
            todo.id === todoToUpdate.id &&
            todo.internetMessageId === todoToUpdate.internetMessageId
                ? new TodoItem({ ...todo, done: !todoToUpdate.done })
                : todo,
        );

        queryClient.setQueryData(queryKeys.dashboardTodos(), newAcceptedTodos);

        const success = await setTodoState(todoToUpdate, !todoToUpdate.done);
        if (success) {
            queryClient.invalidateQueries({
                queryKey: queryKeys.mailThread.detail(
                    openedMail?.internetMessageId,
                ),
            });
        } else {
            queryClient.setQueryData(queryKeys.dashboardTodos(), previousTodos);
        }
    };

    if (!todos) return null;

    const pendingTodoCount = todos.filter((todo) => !todo.done).length;

    return (
        <ExpandableCard
            className="h-40 min-w-65 flex-1"
            icon={todoIcon}
            isExpanded={isExpanded}
            onCollapse={onCollapse}
            onExpand={onExpand}
            title={t("dashboard.widgets.todos", {
                count: pendingTodoCount,
            })}
        >
            <ScrollArea viewportClassName="h-full">
                <div className="flex flex-col gap-1 text-white">
                    {todos.map((todo) => (
                        <DashboardTodoLine
                            key={`${todo.id}-${todo.internetMessageId}`}
                            handleCompletionToggle={handleCompletionToggle}
                            todo={todo}
                        />
                    ))}
                </div>
            </ScrollArea>
        </ExpandableCard>
    );
};

export default DashboardTodos;
